import './UserMenu.scss';

import {
  mdiAccountCircleOutline,
  mdiChevronDown,
  mdiDockLeft,
  mdiDockTop,
  mdiHelpCircleOutline,
  mdiKeyOutline,
  mdiNewspaperVariantOutline,
  mdiViewDashboardOutline
} from '@mdi/js';
import type { PopoverProps } from 'antd';
import { Popover } from 'antd';
import useAuth from 'common/useAuth';
import { useNavigationLayout } from 'components/AppShell/NavigationLayoutProvider';
import Icon from 'components/Icon/Icon';
import Modal from 'components/Modal/Modal';
import UserBadge from 'components/UserBadge/UserBadge';
import { getFullName } from 'helpers/contactPersonHelpers';
import { useIsMultiLanguageEnabled } from 'helpers/featureFlagsHelper';
import { useCustomTranslation } from 'helpers/translationHelper';
import { AppUserPasswordForm } from 'pages/user/settings';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import LanguageSwitcher from './LanguageSwitcher';

interface UserMenuProps {
  compact?: boolean;
  placement?: PopoverProps['placement'];
  onOpenChange?: (open: boolean) => void;
}

const UserMenu = ({ compact, placement = 'bottomLeft', onOpenChange }: UserMenuProps) => {
  const { user, logout } = useAuth();
  const { t, tSetting, tCommon } = useCustomTranslation();
  const { layout, canSwitchLayout, setLayout } = useNavigationLayout();
  const multiLanguageEnabled = useIsMultiLanguageEnabled();

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);

  useEffect(() => {
    onOpenChange?.(popoverIsOpen);
  }, [onOpenChange, popoverIsOpen]);

  if (!user) return null;

  return (
    <>
      <Popover
        trigger="click"
        arrow={false}
        placement={placement}
        classNames={{ root: 'UserMenu' }}
        open={popoverIsOpen}
        onOpenChange={(open) => {
          setPopoverIsOpen(open);
        }}
        content={
          <div className="UserMenu_menu">
            <div className="UserMenu_top">
              <div className="UserMenu_avatar">
                <div className="username">{getFullName(user)}</div>
                <div className="company">{user.companyName}</div>
              </div>
              {multiLanguageEnabled && <LanguageSwitcher />}
            </div>

            <div className="UserMenu_actions">
              {canSwitchLayout &&
                (
                  [
                    { value: 'top', icon: mdiDockTop, label: 'Horní navigace' },
                    { value: 'left', icon: mdiDockLeft, label: 'Levý panel' },
                    {
                      value: 'combined',
                      icon: mdiViewDashboardOutline,
                      label: 'Levý panel a horní lišta'
                    }
                  ] as const
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="UserMenu_item UserMenu_navigationLayout"
                    aria-pressed={layout === option.value}
                    onClick={() => {
                      setPopoverIsOpen(false);
                      onOpenChange?.(false);
                      setLayout(option.value);
                    }}
                  >
                    <Icon path={option.icon} />
                    <span>
                      {tCommon(`navigation_layout_${option.value}`, { defaultValue: option.label })}
                    </span>
                  </button>
                ))}
              <div className="UserMenu_item" onClick={() => setPopoverIsOpen(false)}>
                <Link to="/user/settings">
                  <Icon path={mdiAccountCircleOutline} />
                  <span>{t('mainMenu.user.userSettings')}</span>
                </Link>
              </div>
              <div
                className="UserMenu_item"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setPopoverIsOpen(false);
                  setShowPasswordModal(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPopoverIsOpen(false);
                    setShowPasswordModal(true);
                  }
                }}
              >
                <Icon path={mdiKeyOutline} />
                <span>{t('mainMenu.user.changePassword')}</span>
              </div>
              <div className="UserMenu_item" onClick={() => setPopoverIsOpen(false)}>
                <Link to="/supportTickets">
                  <Icon path={mdiHelpCircleOutline} />
                  <span>{t('mainMenu.user.supportSection')}</span>
                </Link>
              </div>
              <a
                className="UserMenu_item"
                role="button"
                href="https://docs.mwork365.com/cs/"
                tabIndex={0}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setPopoverIsOpen(false)}
              >
                <Icon path={mdiNewspaperVariantOutline} />
                <span>{tSetting('user_menu_docs')}</span>
              </a>
            </div>
            <hr />
            <div className="UserMenu_otherActions">
              <div
                onClick={() => logout()}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && logout()}
                className="UserMenu_item"
                role="button"
                tabIndex={0}
              >
                <span>{t('mainMenu.user.signOut')}</span>
              </div>
            </div>
          </div>
        }
      >
        <button
          type="button"
          className="UserMenu_trigger"
          aria-label={`${getFullName(user)} — ${t('mainMenu.user.userSettings')}`}
          aria-expanded={popoverIsOpen}
        >
          <UserBadge
            colorPaletteColor={user.userColorPalette?.color}
            profilePictureUrl={user.userProfilePictureUrl}
            initials={user.initials}
            hideName={compact}
            name={
              <div className="title">
                <span className="username">{getFullName(user)}</span>
                <span className="companyname">{user.companyName}</span>
              </div>
            }
          />
          {!compact && <Icon path={mdiChevronDown} className="down" />}
        </button>
      </Popover>
      <Modal
        title={t('mainMenu.user.changePassword')}
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      >
        <AppUserPasswordForm closeForm={() => setShowPasswordModal(false)} />
      </Modal>
    </>
  );
};

export default UserMenu;
